// Estado del chat con el servidor MCP, compartido por el botón del header y el panel.
//
// Vive en LayoutWAM: sobrevive a la navegación. Se recuerda en sessionStorage si el
// panel está abierto y qué conversación está activa (los mensajes se recargan del
// backend). Sin habilitado=true desde /api/asistente/estado/, no se muestra nada.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { aceptarTerminos as aceptarTerminosApi, enviarMensaje, ErrorAsistente, getConversacion, getEstado } from './asistenteApi';

const CLAVE_ABIERTO = 'wam.asistente.abierto';
const CLAVE_CONVERSACION = 'wam.asistente.conversacion';

const AsistenteContext = createContext({ habilitado: false });

export const useAsistente = () => useContext(AsistenteContext);

const leer = (clave) => {
  try {
    return window.sessionStorage.getItem(clave);
  } catch {
    return null;
  }
};

const guardar = (clave, valor) => {
  try {
    if (valor === null || valor === undefined || valor === false) window.sessionStorage.removeItem(clave);
    else window.sessionStorage.setItem(clave, String(valor));
  } catch {
    // sin sessionStorage (modo privado): solo se pierde el recuerdo entre recargas
  }
};

export const nombreConsorcio = (c) => (c ? c.domicilio || c.nombre || `Consorcio ${c.id}` : '');

const desdeBackend = (m) => ({
  id: m.id,
  rol: m.rol,
  texto: m.texto,
  herramientas: (m.herramientas || []).map((h, i) => ({ id: `${m.id}-${i}`, ...h, estado: h.error ? 'error' : 'listo' })),
  // Revisión de liquidación: cada herramienta que dejó un archivo vuelve a mostrar sus botones.
  archivos: (m.herramientas || []).filter((h) => h.archivo).map((h) => h.archivo),
  // Acciones propuestas en esta respuesta (con su estado de ahora).
  acciones: (m.herramientas || []).filter((h) => h.accion).map((h) => h.accion),
  fallido: m.fallido,
  error: m.fallido && m.rol === 'user' ? 'Esta pregunta no tuvo respuesta.' : null,
  costoUsd: m.costo_usd,
  tokensEntrada: m.tokens_entrada,
  tokensSalida: m.tokens_salida,
  pendiente: false,
});

let secuencia = 0;
const idLocal = () => `local-${Date.now()}-${(secuencia += 1)}`;

const SIN_CONSORCIOS = [];

// consorcios / onCambiarConsorcio: la lista y el cambio del selector del encabezado de WAM.
// Cambiar el consorcio desde el chat cambia el de toda la pantalla (una sola fuente de verdad).
export const AsistenteProvider = ({
  children,
  consorcio = null,
  consorcios = SIN_CONSORCIOS,
  onCambiarConsorcio = null,
  onEstado = null,
}) => {
  const [estado, setEstado] = useState(null);
  const [abierto, setAbierto] = useState(() => leer(CLAVE_ABIERTO) === '1');
  const [conversacionId, setConversacionId] = useState(() => leer(CLAVE_CONVERSACION));
  const [mensajes, setMensajes] = useState([]);
  const [enCurso, setEnCurso] = useState(false);
  const [fase, setFase] = useState(null); // 'pensando' | 'herramienta' | 'escribiendo'
  const [nota, setNota] = useState('');
  const [ampliado, setAmpliado] = useState(null);
  // Sube cada vez que cambia una acción: el panel de acciones del módulo se recarga.
  const [versionAcciones, setVersionAcciones] = useState(0);
  const abortRef = useRef(null);
  const habilitado = Boolean(estado?.habilitado);
  // Con términos pendientes el panel muestra los términos en vez del chat.
  const terminos = estado?.terminos || null;

  // La barra lateral (fuera de este provider) muestra "Asistente" solo si está habilitado.
  useEffect(() => {
    if (onEstado && estado) onEstado(estado);
  }, [estado, onEstado]);

  useEffect(() => {
    let vivo = true;
    getEstado()
      .then((datos) => vivo && setEstado(datos))
      .catch(() => vivo && setEstado({ habilitado: false }));
    return () => {
      vivo = false;
    };
  }, []);

  const recargarEstado = useCallback(async () => {
    try {
      setEstado(await getEstado());
    } catch {
      // se mantiene el estado anterior
    }
  }, []);

  const aceptarTerminos = useCallback(
    async (nivel) => {
      if (!terminos) return;
      await aceptarTerminosApi(terminos.version, nivel);
      await recargarEstado();
    },
    [terminos, recargarEstado]
  );

  // Al recargar la página, vuelve a dibujar la conversación activa.
  useEffect(() => {
    if (!habilitado || terminos || !conversacionId || mensajes.length) return;
    let vivo = true;
    getConversacion(conversacionId)
      .then((datos) => vivo && setMensajes((datos.mensajes || []).map(desdeBackend)))
      .catch(() => {
        if (!vivo) return;
        setConversacionId(null);
        guardar(CLAVE_CONVERSACION, null);
      });
    return () => {
      vivo = false;
    };
  }, [habilitado, terminos, conversacionId, mensajes.length]);

  const abrir = useCallback(() => {
    setAbierto(true);
    guardar(CLAVE_ABIERTO, '1');
  }, []);
  const cerrar = useCallback(() => {
    setAbierto(false);
    guardar(CLAVE_ABIERTO, null);
  }, []);
  const alternar = useCallback(() => {
    setAbierto((prev) => {
      guardar(CLAVE_ABIERTO, prev ? null : '1');
      return !prev;
    });
  }, []);

  // Ctrl+K / Cmd+K abre y cierra el panel. El listener se registra una vez y mira
  // si está habilitado en el momento (sin carrera con la carga del estado).
  const habilitadoRef = useRef(false);
  habilitadoRef.current = habilitado;
  useEffect(() => {
    const onKey = (e) => {
      if (!habilitadoRef.current) return;
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        alternar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [alternar]);

  useEffect(() => () => abortRef.current && abortRef.current.abort(), []);

  // Una acción cambió (confirmada, cancelada, deshecha): se actualiza en todos los mensajes.
  const actualizarAccion = useCallback((accion) => {
    if (!accion?.id) return;
    setMensajes((prev) =>
      prev.map((m) =>
        (m.acciones || []).some((x) => x.id === accion.id)
          ? { ...m, acciones: m.acciones.map((x) => (x.id === accion.id ? accion : x)) }
          : m
      )
    );
    setVersionAcciones((v) => v + 1);
  }, []);

  const abrirConversacion = useCallback((id) => {
    if (abortRef.current) abortRef.current.abort();
    setMensajes([]);
    setConversacionId(String(id));
    guardar(CLAVE_CONVERSACION, id);
  }, []);

  const nuevaConversacion = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setMensajes([]);
    setConversacionId(null);
    guardar(CLAVE_CONVERSACION, null);
    setEnCurso(false);
    setFase(null);
    setNota('');
  }, []);

  const actualizarRespuesta = (id, cambio) =>
    setMensajes((prev) => prev.map((m) => (m.id === id ? { ...m, ...(typeof cambio === 'function' ? cambio(m) : cambio) } : m)));

  const enviar = useCallback(
    async (textoCrudo) => {
      const texto = String(textoCrudo || '').trim();
      if (!texto || enCurso || !habilitado || terminos) return;
      const idRespuesta = idLocal();
      setMensajes((prev) => [
        ...prev,
        { id: idLocal(), rol: 'user', texto, herramientas: [], pendiente: false },
        { id: idRespuesta, rol: 'assistant', texto: '', herramientas: [], pendiente: true },
      ]);
      setEnCurso(true);
      setFase('pensando'); // se ve apenas se envía, antes del primer evento
      setNota('');
      const controlador = new AbortController();
      abortRef.current = controlador;

      const onEvento = ({ nombre, datos }) => {
        if (nombre === 'inicio') {
          setConversacionId(String(datos.conversacion_id));
          guardar(CLAVE_CONVERSACION, datos.conversacion_id);
        } else if (nombre === 'herramienta') {
          setFase('herramienta');
          setNota('');
          actualizarRespuesta(idRespuesta, (m) => ({
            herramientas: [...m.herramientas, { id: datos.id, nombre: datos.nombre, etiqueta: datos.etiqueta, estado: 'corriendo' }],
          }));
        } else if (nombre === 'herramienta_fin') {
          actualizarRespuesta(idRespuesta, (m) => ({
            herramientas: m.herramientas.map((h) =>
              h.id === datos.id ? { ...h, etiqueta: datos.etiqueta, estado: datos.error ? 'error' : 'listo' } : h
            ),
          }));
          setFase('pensando');
        } else if (nombre === 'accion') {
          actualizarRespuesta(idRespuesta, (m) => ({ acciones: [...(m.acciones || []), datos] }));
          setVersionAcciones((v) => v + 1);
        } else if (nombre === 'archivo') {
          actualizarRespuesta(idRespuesta, (m) => ({ archivos: [...(m.archivos || []), datos] }));
        } else if (nombre === 'nota') {
          setNota((prev) => `${prev}${datos.texto}`);
        } else if (nombre === 'texto') {
          setFase('escribiendo');
          actualizarRespuesta(idRespuesta, (m) => ({ texto: `${m.texto}${datos.delta}` }));
        } else if (nombre === 'fin') {
          actualizarRespuesta(idRespuesta, {
            pendiente: false,
            costoUsd: datos.costo_usd,
            tokensEntrada: datos.tokens_entrada,
            tokensSalida: datos.tokens_salida,
          });
          setEstado((prev) =>
            prev
              ? { ...prev, gastado_hoy_usd: (Number(prev.gastado_hoy_usd || 0) + Number(datos.costo_usd || 0)).toFixed(4) }
              : prev
          );
        } else if (nombre === 'error') {
          actualizarRespuesta(idRespuesta, { pendiente: false, error: datos.mensaje, detalle: datos.detalle });
        }
      };

      try {
        // Por axios: renueva el JWT si venció y trae el gasto del día.
        try {
          setEstado(await getEstado());
        } catch {
          // si falla, el POST devuelve el error que corresponda
        }
        await enviarMensaje(
          {
            texto,
            conversacion_id: conversacionId || null,
            consorcio_id: consorcio?.id || null,
            consorcio_nombre: nombreConsorcio(consorcio),
          },
          { onEvento, signal: controlador.signal }
        );
        actualizarRespuesta(idRespuesta, (m) => (m.pendiente ? { pendiente: false, error: m.error || 'La respuesta se cortó. Probá de nuevo.' } : {}));
      } catch (err) {
        if (err?.name === 'AbortError') return;
        // Se publicó otra versión de los términos mientras el panel estaba abierto.
        if (err instanceof ErrorAsistente && err.codigo === 'terminos_pendientes') recargarEstado();
        const mensaje = err instanceof ErrorAsistente ? err.message : 'No se pudo conectar con el asistente. Revisá la conexión.';
        actualizarRespuesta(idRespuesta, { pendiente: false, error: mensaje });
      } finally {
        if (abortRef.current === controlador) abortRef.current = null;
        setEnCurso(false);
        setFase(null);
        setNota('');
      }
    },
    [enCurso, habilitado, terminos, conversacionId, consorcio, recargarEstado]
  );

  const valor = useMemo(
    () => ({
      habilitado,
      estado,
      terminos,
      aceptarTerminos,
      abierto,
      abrir,
      cerrar,
      alternar,
      consorcio,
      consorcios,
      cambiarConsorcio: onCambiarConsorcio,
      mensajes,
      enCurso,
      fase,
      nota,
      enviar,
      nuevaConversacion,
      ampliado,
      ampliar: setAmpliado,
      actualizarAccion,
      abrirConversacion,
      conversacionId,
      versionAcciones,
    }),
    [habilitado, estado, terminos, aceptarTerminos, abierto, abrir, cerrar, alternar, consorcio, consorcios, onCambiarConsorcio, mensajes, enCurso, fase, nota, enviar, nuevaConversacion, ampliado, actualizarAccion, abrirConversacion, conversacionId, versionAcciones]
  );

  return <AsistenteContext.Provider value={valor}>{children}</AsistenteContext.Provider>;
};
